// AW search panel (snippets/aw-search-drawer.liquid).
// Opens from any [aria-label="Search"] button. Before typing it shows trending,
// merch and brand chips; while typing it loads the predictive-search section;
// with no match it offers Val's picks and a chat button.
// Searches are sent to PostHog (search_performed, search_suggestion_clicked,
// search_result_clicked) for the Searches section of the Website Behavior dashboard.
document.addEventListener("DOMContentLoaded", () => {
    const drawer = document.getElementById("awSearchDrawer");
    const input = document.getElementById("awPredictiveSearchInput");
    const results = document.getElementById("awPredictiveSearchResults");
    const discover = document.getElementById("awSearchDiscover");
    const closeBtn = document.getElementById("awSearchClose");
    if (!drawer || !input || !results) return;

    const MIN_CHARS = 2;
    const track = (event, props) => {
        try {
            if (window.posthog && typeof window.posthog.capture === "function") window.posthog.capture(event, props);
        } catch (e) { /* tracking must never break search */ }
    };

    // ---------- open and close ----------
    function open() {
        drawer.classList.add("active");
        document.body.classList.add("aw-search-open");
        startHints();
        setTimeout(() => input.focus(), 150);
    }
    function close() {
        drawer.classList.remove("active");
        document.body.classList.remove("aw-search-open");
        stopHints();
        flushTracking();
    }
    // Buttons labelled "Search" open the panel; the panel's own input shares the label, so skip it.
    document.querySelectorAll('[aria-label="Search"]').forEach((btn) => {
        if (btn === input || drawer.contains(btn)) return;
        btn.addEventListener("click", open);
    });
    if (closeBtn) closeBtn.addEventListener("click", close);
    document.addEventListener("keydown", (e) => { if (e.key === "Escape" && drawer.classList.contains("active")) close(); });
    drawer.addEventListener("click", (e) => { if (e.target === drawer) close(); });

    // ---------- rotating example in the search box ----------
    const examples = (input.dataset.examples || "").split(",").map((s) => s.trim()).filter(Boolean);
    const basePlaceholder = input.getAttribute("placeholder");
    let hintTimer = null;
    let hintIndex = 0;
    function startHints() {
        if (!examples.length || hintTimer) return;
        if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
        hintTimer = setInterval(() => {
            if (input.value) return;
            input.setAttribute("placeholder", 'Try "' + examples[hintIndex % examples.length] + '"');
            hintIndex++;
        }, 2600);
    }
    function stopHints() {
        clearInterval(hintTimer);
        hintTimer = null;
        input.setAttribute("placeholder", basePlaceholder);
    }

    // ---------- highlight the typed words in result titles ----------
    function highlight(root, query) {
        const words = query.toLowerCase().split(/\s+/).filter((w) => w.length >= MIN_CHARS);
        if (!words.length) return;
        const pattern = new RegExp("(" + words.map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|") + ")", "gi");
        root.querySelectorAll("[data-aw-highlight]").forEach((el) => {
            const text = el.textContent;
            const frag = document.createDocumentFragment();
            let last = 0;
            text.replace(pattern, (match, _g, offset) => {
                frag.appendChild(document.createTextNode(text.slice(last, offset)));
                const mark = document.createElement("mark");
                mark.textContent = match;
                frag.appendChild(mark);
                last = offset + match.length;
                return match;
            });
            frag.appendChild(document.createTextNode(text.slice(last)));
            el.replaceChildren(frag);
        });
    }

    // ---------- tracking: one event per finished search ----------
    let pending = null;      // { query, results } waiting to be sent
    let lastSent = "";
    let trackTimer = null;
    function queueTracking(query, count) {
        pending = { query: query, results: count };
        clearTimeout(trackTimer);
        trackTimer = setTimeout(flushTracking, 1500);
    }
    function flushTracking() {
        clearTimeout(trackTimer);
        if (!pending || pending.query.toLowerCase() === lastSent) { pending = null; return; }
        lastSent = pending.query.toLowerCase();
        track("search_performed", { query: lastSent, results: pending.results, source: "search_panel" });
        pending = null;
    }

    // ---------- predictive search ----------
    let debounce;
    let requestId = 0;
    function showDiscover(show) {
        if (discover) discover.hidden = !show;
    }

    input.addEventListener("input", () => {
        clearTimeout(debounce);
        const query = input.value.trim();
        if (query.length < MIN_CHARS) {
            requestId++;
            results.replaceChildren();
            showDiscover(true);
            return;
        }
        debounce = setTimeout(() => runSearch(query), 220);
    });

    async function runSearch(query) {
        const id = ++requestId;
        results.classList.add("is-loading");
        try {
            const url = "/search/suggest?q=" + encodeURIComponent(query) +
                "&resources[type]=product,collection&resources[limit]=6&resources[limit_scope]=each" +
                "&resources[options][prefix]=last&section_id=predictive-search";
            const response = await fetch(url);
            const text = await response.text();
            if (id !== requestId) return; // a newer search started
            const doc = new DOMParser().parseFromString(text, "text/html");
            const markup = doc.querySelector("#shopify-section-predictive-search .aw-search-results");
            showDiscover(false);
            if (markup) {
                results.replaceChildren(document.importNode(markup, true));
                highlight(results, query);
                queueTracking(query, Number(markup.dataset.awResultCount || 0));
            } else {
                results.replaceChildren();
            }
        } catch (error) {
            if (id !== requestId) return;
            console.error("Predictive search error:", error);
            const p = document.createElement("p");
            p.className = "aw-search-message";
            p.textContent = "Search isn't responding. Press Enter to see all results.";
            results.replaceChildren(p);
        } finally {
            if (id === requestId) results.classList.remove("is-loading");
        }
    }

    // ---------- clicks inside the panel ----------
    drawer.addEventListener("click", (e) => {
        const chip = e.target.closest("[data-aw-search-term]");
        if (chip) {
            track("search_suggestion_clicked", { term: chip.dataset.awSearchTerm.toLowerCase(), source: chip.dataset.awSearchSource || "chip" });
            return; // let the link go to the results page
        }
        const item = e.target.closest("#awPredictiveSearchResults a");
        if (item) {
            const links = Array.prototype.slice.call(results.querySelectorAll("a"));
            flushTracking();
            track("search_result_clicked", { query: input.value.trim().toLowerCase(), url: item.getAttribute("href"), position: links.indexOf(item) + 1 });
            return;
        }
        if (e.target.closest("[data-aw-ask-val]")) {
            track("search_ask_val_clicked", { query: input.value.trim().toLowerCase() });
            close();
            if (window.GorgiasChat && typeof window.GorgiasChat.open === "function") window.GorgiasChat.open();
            else window.location.href = "/pages/contact";
        }
    });

    // Enter goes to the full results page; send the search first.
    const form = drawer.querySelector("form");
    if (form) form.addEventListener("submit", () => {
        const q = input.value.trim();
        if (q.length >= MIN_CHARS) { pending = { query: q, results: pending ? pending.results : null }; flushTracking(); }
    });
});
